import { forwardRef, type InputHTMLAttributes } from 'react'
import { cn } from '../../lib/utils'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, id, ...props }, ref) => {
    const inputId = id || props.name
    const errorId = error ? `${inputId}-error` : undefined
    const hintId = hint && !error ? `${inputId}-hint` : undefined
    const describedBy = errorId || hintId

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          className={cn(
            'block w-full rounded-lg border px-3 py-2 text-sm transition-colors duration-150',
            'placeholder:text-[var(--text-muted)]',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            error
              ? 'border-[var(--error)] focus:border-[var(--error)] focus:ring-[var(--error)]/20'
              : 'border-[var(--border-default)] focus:border-[var(--text-primary)] focus:ring-[var(--text-primary)]/20',
            'bg-[var(--bg-elevated)]',
            'text-[var(--text-primary)]',
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-[var(--bg-elevated-2)]',
            className
          )}
          {...props}
        />
        {error && (
          <p id={errorId} role="alert" className="mt-1.5 text-sm text-[var(--error)]">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={hintId} className="mt-1.5 text-sm text-[var(--text-secondary)]">{hint}</p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
